import jsPDF from 'jspdf';

// Generate ticket PDF for sales orders
export const generateTicketPDF = (order, clientName, items = [], payments = []) => {
  // Create PDF with small receipt format (80mm width, similar to thermal receipt)
  const doc = new jsPDF({ unit: 'mm', format: [80, 200] });
  
  // Page dimensions
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 5;
  const contentWidth = pageWidth - (2 * margin);
  
  // Colors
  const primaryColor = [0, 51, 102]; // Dark blue for EPOPTIQUE
  const textColor = [51, 51, 51];
  const lightGray = [245, 245, 245];
  
  // Helper functions
  const cleanAmount = (amount) => {
    if (!amount) return 0;
    let cleanAmount = String(amount);
    cleanAmount = cleanAmount.replace(/^\d+\//, '');
    cleanAmount = cleanAmount.replace(/[^\d,.-]/g, '');
    cleanAmount = cleanAmount.replace(',', '.');
    const numAmount = parseFloat(cleanAmount);
    return isNaN(numAmount) ? 0 : numAmount;
  };
  
  const formatCurrency = (amount) => {
    const cleanedAmount = cleanAmount(amount);
    if (cleanedAmount === 0) return '0,00';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'decimal',
      useGrouping: false
    }).format(cleanedAmount);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  const safeText = (text) => {
    if (!text) return '';
    return String(text).replace(/[^\x00-\x7F]/g, '');
  };
  
  // Calculate total first for advance payment display
  let total = 0;
  let totalQuantity = 0;
  
  if (items && items.length > 0) {
    items.forEach((item) => {
      const cleanPrice = cleanAmount(item.price || 0);
      const cleanQuantity = parseInt(item.quantity || 1);
      const itemTotal = cleanPrice * cleanQuantity;
      total += itemTotal;
      totalQuantity += cleanQuantity;
    });
  } else {
    // Fallback for orders without detailed items
    const amount = cleanAmount(order.total || 0);
    total = amount;
    totalQuantity = 1;
  }
  
  let yPosition = 10;
  
  // Header - Company Name (more prominent)
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('EPOPTIQUE', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  // Contact information
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  doc.text('0522-22-55-25 / 07-64-200-156', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 4;
  
  // Address
  doc.setFontSize(7);
  doc.text('8 RUE ABOU ALWAQT KHALAF, BOURGOGNE CASABLANCA', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  // Transaction details (cleaner spacing)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  doc.text(`Ticket n°${order.id} du ${formatDate(order.created_at)}-${formatTime(order.created_at)}`, margin, yPosition);
  yPosition += 5;
  doc.text(`Client : ${clientName || 'CLIENT COMPTANT'}`, margin, yPosition);
  yPosition += 5;
  
  // Add advance payment info under client if exists
  const totalPaid = payments.reduce((sum, p) => sum + cleanAmount(p.amount), 0);
  if (totalPaid > 0 && totalPaid < total) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Avance :', margin, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(totalPaid), pageWidth - margin, yPosition, { align: 'right' });
    yPosition += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Reste :', margin, yPosition);
    doc.setFont('helvetica', 'bold');
    const remaining = total - totalPaid;
    doc.text(formatCurrency(remaining), pageWidth - margin, yPosition, { align: 'right' });
    yPosition += 5;
  }
  
  yPosition += 5;
  
  // Separator line (cleaner)
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;
  
  // Items table header (better alignment)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Article', margin, yPosition);
  doc.text('Qté', margin + 40, yPosition);
  doc.text('Prix', margin + 55, yPosition);
  yPosition += 5;
  
  // Items (cleaner formatting)
  doc.setFont('helvetica', 'normal');
  
  if (items && items.length > 0) {
    items.forEach((item) => {
      const cleanPrice = cleanAmount(item.price || 0);
      const cleanQuantity = parseInt(item.quantity || 1);
      
      // Item name (cleaner)
      doc.setFontSize(9);
      doc.text(safeText(item.product_name || `Produit ${item.product_id}`), margin, yPosition);
      yPosition += 4;
      
      // Item details (if available)
      if (item.side && item.side !== 'none') {
        const sideText = item.side === 'left' ? 'Oeil gauche' : 'Oeil droit';
        doc.setFontSize(8);
        doc.text(`[${sideText}]`, margin + 2, yPosition);
        yPosition += 3;
      }
      
      // Quantity and price (better alignment)
      doc.setFontSize(9);
      doc.text(String(cleanQuantity), margin + 40, yPosition - 2);
      doc.text(formatCurrency(cleanPrice), margin + 55, yPosition - 2);
      yPosition += 3;
    });
  } else {
    // Fallback for orders without detailed items
    const amount = cleanAmount(order.total || 0);
    doc.setFontSize(9);
    doc.text('Montant de la commande', margin, yPosition);
    doc.text('1', margin + 40, yPosition);
    doc.text(formatCurrency(amount), margin + 55, yPosition);
    yPosition += 5;
  }
  
  // Separator line
  yPosition += 3;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 6;
  
  // Summary (cleaner layout)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Nombre d\'articles', margin, yPosition);
  doc.text(String(totalQuantity), pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Total TTC en MAD', margin, yPosition);
  doc.text(formatCurrency(total), pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 5;
  
  // Payment method (cleaner)
  const paymentMode = payments.length > 0 ? payments[0].mode : 'espece';
  const paymentLabels = {
    'espece': 'Espèce',
    'cheque': 'Chèque',
    'carte_bancaire': 'Carte bancaire'
  };
  doc.setFont('helvetica', 'normal');
  doc.text('Règlement :', margin, yPosition);
  doc.setFont('helvetica', 'bold');
  doc.text(paymentLabels[paymentMode] || 'Espèce', pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 10;
  
  // Footer message (cleaner)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Merci pour votre achat.', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 5;
  doc.text('Nous espérons vous revoir', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 4;
  doc.text('rapidement dans notre boutique !', pageWidth / 2, yPosition, { align: 'center' });
  
  // Save the PDF
  doc.save(`Ticket_${order.id}.pdf`);
}; 